export type GpuTier = 'low' | 'medium' | 'high';

interface NavigatorWithHints extends Navigator {
  readonly deviceMemory?: number;
  readonly connection?: { readonly saveData?: boolean };
}

/** Rendus sans accélération matérielle (Chrome, Mesa, Windows sans pilote). */
const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software|basic render/i;

interface DeviceTraits {
  readonly prefersReducedMotion: boolean;
  readonly portrait: boolean;
  readonly webgl2: boolean;
  readonly gpuTier: GpuTier;
}

/**
 * Ce que l'appareil et les préférences du visiteur permettent, lu une seule
 * fois au démarrage : scène en temps réel ou affiche fixe, cadrage paysage ou portrait.
 * En recette, `?quality=low|medium|high` force le niveau.
 */
export class DeviceProfile {
  readonly prefersReducedMotion: boolean;
  readonly portrait: boolean;
  readonly webgl2: boolean;
  readonly gpuTier: GpuTier;

  private constructor(traits: DeviceTraits) {
    this.prefersReducedMotion = traits.prefersReducedMotion;
    this.portrait = traits.portrait;
    this.webgl2 = traits.webgl2;
    this.gpuTier = traits.gpuTier;
  }

  static detect(win: Window): DeviceProfile {
    const nav = win.navigator as NavigatorWithHints;
    const gpu = DeviceProfile.probeGpu(win.document);
    const forced = DeviceProfile.parseTier(new URLSearchParams(win.location.search).get('quality'));
    const coarsePointer = win.matchMedia('(pointer: coarse)').matches;

    const gpuTier =
      forced ??
      DeviceProfile.estimateTier({
        softwareRenderer: gpu.softwareRenderer,
        saveData: nav.connection?.saveData ?? false,
        coarsePointer,
        cores: nav.hardwareConcurrency,
        memoryGb: nav.deviceMemory ?? 8,
      });

    return new DeviceProfile({
      prefersReducedMotion: win.matchMedia('(prefers-reduced-motion: reduce)').matches,
      portrait: win.matchMedia('(max-aspect-ratio: 4/5)').matches,
      webgl2: gpu.webgl2,
      gpuTier,
    });
  }

  /** Le décor cosmique est calculé pixel par pixel : il faut un GPU correct. */
  get canRenderCosmos(): boolean {
    return this.webgl2 && !this.prefersReducedMotion && this.gpuTier !== 'low';
  }

  private static estimateTier(signals: {
    softwareRenderer: boolean;
    saveData: boolean;
    coarsePointer: boolean;
    cores: number;
    memoryGb: number;
  }): GpuTier {
    if (signals.softwareRenderer || signals.saveData) return 'low';
    if (signals.cores <= 2 || signals.memoryGb <= 2) return 'low';
    if (signals.coarsePointer || signals.cores <= 4 || signals.memoryGb <= 4) return 'medium';
    return 'high';
  }

  private static probeGpu(doc: Document): { webgl2: boolean; softwareRenderer: boolean } {
    const gl = doc.createElement('canvas').getContext('webgl2');
    if (!gl) return { webgl2: false, softwareRenderer: true };
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : '';
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return { webgl2: true, softwareRenderer: SOFTWARE_RENDERER.test(renderer) };
  }

  private static parseTier(value: string | null): GpuTier | null {
    return value === 'low' || value === 'medium' || value === 'high' ? value : null;
  }
}
